import {
  PrivateLabelInfo,
  StorageItemAuditUserInfo,
  StorageItems,
  StorageItemSiteInfo,
} from '@/entities/storage.items';
import SSOToken from '@/entities/ssokoken';
import Logger from '@/utils/logger.utils';
import siteConfigService from '@/services/site.config.service';
import { NWAccessUserIdAndToken } from '@ol/ol-ui-core';

class StorageService {
    private apiStorage: Storage = window.localStorage;

    constructor() {
      Logger.debug('creating new instance of StorageItemsServices');
    }

    private static siteInfoStorageName: string = 'siteInfo';
    private static atcExtraHeaderStorageName: string = 'atcExtraHeader';
    private static qrCodeTokenStorageName: string = 'qrCodeToken';
    private static auditUserInfoStorageName: string = 'auditUserInfo';

    public async saveStorageItems(storageItems: StorageItems): Promise<void> {
      const siteInfo: StorageItemSiteInfo = storageItems.getSiteInfo();
      const atcExtraHeader: string = storageItems.getAtcExtraHeader();
      if (siteInfo) {
        await this.apiStorage.setItem(StorageService.siteInfoStorageName, JSON.stringify(siteInfo));
      }
      if (atcExtraHeader) {
        await this.apiStorage.setItem(StorageService.atcExtraHeaderStorageName, atcExtraHeader);
      }
    }

    public async removeSiteInfo(): Promise<void> {
      await this.apiStorage.removeItem(StorageService.siteInfoStorageName);
    }

    public async removeAtcExtraHeader(): Promise<void> {
      await this.apiStorage.removeItem(StorageService.atcExtraHeaderStorageName);
    }
    public async saveAtcExtraHeader(atcExtraHeader: string): Promise<void> {
      const storageItems: StorageItems = await this.getStorageItems();
      storageItems.setAtcExtraHeader(atcExtraHeader);
      await this.apiStorage.setItem(StorageService.atcExtraHeaderStorageName, atcExtraHeader);
    }

    public async hasSiteInfoInStorage(): Promise<boolean> {
      let siteInfoStr: string = this.apiStorage.getItem(StorageService.siteInfoStorageName);
      if (!siteInfoStr) {
        if (!!process.env.NODE_ENV && (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'e2etest')) {
          this.initDefaultStorageItems();
        } else {
          await siteConfigService.setSiteInfoInLocalStorage();
        }
        siteInfoStr = this.apiStorage.getItem(StorageService.siteInfoStorageName);
      } else {
        siteInfoStr = siteInfoStr.trim();
      }
      return siteInfoStr !== '';
    }

    public async getStorageItems(): Promise<StorageItems> {
      let siteInfoStr: string = await this.apiStorage.getItem(StorageService.siteInfoStorageName) || '';
      if (siteInfoStr) {
        siteInfoStr = siteInfoStr.trim();
      }
      if (!siteInfoStr) {
        Logger.debug(`process.env.NODE_ENV=${process.env.NODE_ENV}`);
        if (!!process.env.NODE_ENV && (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'e2etest')) {
          this.initDefaultStorageItems();
          siteInfoStr = this.apiStorage.getItem(StorageService.siteInfoStorageName) || '';
        } else {
          await siteConfigService.setSiteInfoInLocalStorage();
        }
      }
      return this.setSiteInfoStrStorageItems(siteInfoStr);
    }

    public async getStorageItemsIfExists(): Promise<StorageItems> {
      let siteInfoStr: string = await this.apiStorage.getItem(StorageService.siteInfoStorageName) || '';
      if (siteInfoStr) {
        siteInfoStr = siteInfoStr.trim();
      }
      if (!siteInfoStr) {
        return null;
      }
      return this.setSiteInfoStrStorageItems(siteInfoStr);
    }

    private setSiteInfoStrStorageItems(siteInfoStr: string): StorageItems {
      if (siteInfoStr === '') {
        return new StorageItems();
      }
      const storageItems: StorageItems = new StorageItems();
      storageItems.setSiteInfo(Object.assign(new StorageItemSiteInfo(), JSON.parse(siteInfoStr)));
      const atcExtraHeader: string = this.apiStorage.getItem(StorageService.atcExtraHeaderStorageName) || '';
      if (atcExtraHeader && atcExtraHeader.trim() !== '') {
        storageItems.setAtcExtraHeader(atcExtraHeader);
      }
      const auditUserInfoStr: string = this.apiStorage.getItem(StorageService.auditUserInfoStorageName) || '';
      if (auditUserInfoStr !== '') {
        storageItems.setAuditUserInfo(Object.assign(new StorageItemAuditUserInfo(), JSON.parse(auditUserInfoStr)));
      }
      return storageItems;
    }

    public async saveLanguageCode(languageCode: string): Promise<void> {
      const storageItemSiteInfo: StorageItemSiteInfo = await this.getSiteInfo();
      storageItemSiteInfo.languageCode = languageCode;
      await this.apiStorage.setItem(StorageService.siteInfoStorageName, JSON.stringify(storageItemSiteInfo));
    }

    public async savePrivateLabel(plId: number, plName: string): Promise<void> {
      const storageItems: StorageItems = await this.getStorageItems();
      const siteInfo: StorageItemSiteInfo = storageItems.getSiteInfo() || new StorageItemSiteInfo();
      const privateLabelInfo: PrivateLabelInfo = siteInfo.privateLabelInfo || new PrivateLabelInfo();
      if (privateLabelInfo.id && privateLabelInfo.id !== plId) {
        await siteConfigService.setSiteInfoInLocalStorage(plId);
      } else {
        privateLabelInfo.id = plId;
        privateLabelInfo.name = plName;
        siteInfo.privateLabelInfo = privateLabelInfo;
        storageItems.setSiteInfo(siteInfo);
        await this.saveStorageItems(storageItems);
      }
    }

    public saveFromPage(from:string):void{
      localStorage.setItem('fromPageNext', from);
    }

    public savePageTitle(pageTitle:string):void{
      localStorage.setItem('pageTitle', pageTitle);
    }

    public async getAtcExtraHeader(): Promise<string> {
      const storageItems: StorageItems = await this.getStorageItems();
      const atcExtraHeader: string = storageItems.getAtcExtraHeader();
      return atcExtraHeader;
    }

    public async getUserProfileAuditUserName(): Promise<string> {
      const storageItems: StorageItems = await this.getStorageItems();
      const auditUserInfo: StorageItemAuditUserInfo = storageItems.getAuditUserInfo();
      return auditUserInfo ? auditUserInfo.auditUser : '';
    }

    public async getPrivateLabelId(): Promise<number> {
      const storageItems: StorageItems = await this.getStorageItems();
      const plId: number = +((storageItems.getSiteInfo() || new StorageItemSiteInfo()).privateLabelInfo || new PrivateLabelInfo()).id;
      return plId;
    }

    public async getSiteInfo(): Promise<StorageItemSiteInfo> {
      const storageItems: StorageItems = await this.getStorageItems();

      return storageItems.getSiteInfo();
    }

    public async hasAtcExtraHeader(): Promise<boolean> {
      const storageItems: StorageItems = await this.getStorageItems();
      return !!(storageItems.getAtcExtraHeader()) && storageItems.getAtcExtraHeader() !== '';
    }

    public async getLanguageCode(): Promise<string> {
      const storageItemSiteInfo: StorageItemSiteInfo = await this.getSiteInfo();
      return storageItemSiteInfo.languageCode;
    }

    private initDefaultStorageItems(): void {
      const storageItems: StorageItems = new StorageItems();
      const siteInfo: StorageItemSiteInfo = new StorageItemSiteInfo();
      const privateLabelInfo: PrivateLabelInfo = new PrivateLabelInfo();
      siteInfo.languageCode = 'en';
      privateLabelInfo.id = +process.env.VUE_APP_SITE_PLID;
      privateLabelInfo.name = process.env.VUE_APP_SITE_NAME;
      privateLabelInfo.phone = +process.env.VUE_APP_SITE_PHONE;
      privateLabelInfo.email = process.env.VUE_APP_SITE_EMAIL;
      privateLabelInfo.countryId = +process.env.VUE_APP_SITE_COUNTRY_ID;
      siteInfo.siteConfiguration = process.env.VUE_APP_SITE_CONFIGURATION_URL;
      siteInfo.privateLabelInfo = privateLabelInfo;
      storageItems.setSiteInfo(siteInfo);
      this.apiStorage.setItem(StorageService.siteInfoStorageName, JSON.stringify(siteInfo));
    }

    public async saveQRCodeToken(token: string): Promise<void> {
      await this.apiStorage.setItem(StorageService.qrCodeTokenStorageName, token);
    }

    public async getQRCodeToken(): Promise<string> {
      return this.apiStorage.getItem(StorageService.qrCodeTokenStorageName);
    }
}

/* Export a singleton instance in the global namespace */
export default new StorageService();

